/**
 * Transition Rules: EnergyPlus 9.2 to 9.3
 *
 * Major changes: Fuel type standardization, object renames, EMS function updates
 */

import type { VersionTransition, ObjectTransitionRule } from '../types';
import { createCopyMappings, createShiftedCopyMappings } from '../types';

// Fuel type mapping (old → new)
const FUEL_TYPE_MAP: Record<string, string> = {
  'ELECTRICITY': 'Electricity',
  'ELECTRIC': 'Electricity',
  'NATURALGAS': 'NaturalGas',
  'GAS': 'NaturalGas',
  'DIESEL': 'Diesel',
  'GASOLINE': 'Gasoline',
  'COAL': 'Coal',
  'FUELOILNO1': 'FuelOilNo1',
  'FUELOIL#1': 'FuelOilNo1',
  'FUELOILNO2': 'FuelOilNo2',
  'FUELOIL#2': 'FuelOilNo2',
  'PROPANE': 'Propane',
  'PROPANEGAS': 'Propane',
  'OTHERFUEL1': 'OtherFuel1',
  'OTHERFUEL2': 'OtherFuel2',
  'STEAM': 'Steam',
  'DISTRICTHEATING': 'DistrictHeating',
  'DISTRICTCOOLING': 'DistrictCooling',
};

function standardizeFuelType(value: string): string {
  const upper = value.toUpperCase().replace(/\s+/g, '');
  return FUEL_TYPE_MAP[upper] || value;
}

// GlobalGeometryRules abbreviation expansion
const GEOMETRY_RULES_MAP: Record<string, string> = {
  'ULC': 'UpperLeftCorner',
  'URC': 'UpperRightCorner',
  'LLC': 'LowerLeftCorner',
  'LRC': 'LowerRightCorner',
  'CCW': 'Counterclockwise',
  'CW': 'Clockwise',
  'WCS': 'World',
  'WORLD': 'World',
  'RELATIVE': 'Relative',
};

function expandGeometryAbbreviation(value: string): string {
  const upper = value.toUpperCase().replace(/\s+/g, '');
  return GEOMETRY_RULES_MAP[upper] || value;
}

export function createTransition_9_2_to_9_3(): VersionTransition {
  const objectRules = new Map<string, ObjectTransitionRule>();
  const variableMappings = new Map<string, string>();
  const deletedObjects = new Set<string>();
  const renamedObjects = new Map<string, string>();

  // Object rename: AirTerminal:SingleDuct:Uncontrolled → AirTerminal:SingleDuct:ConstantVolume:NoReheat
  renamedObjects.set('AIRTERMINAL:SINGLEDUCT:UNCONTROLLED', 'AirTerminal:SingleDuct:ConstantVolume:NoReheat');

  // Object rename: HeatPump:WaterToWater:EIR:Heating → HeatPump:PlantLoop:EIR:Heating
  renamedObjects.set('HEATPUMP:WATERTOWATER:EIR:HEATING', 'HeatPump:PlantLoop:EIR:Heating');
  renamedObjects.set('HEATPUMP:WATERTOWATER:EIR:COOLING', 'HeatPump:PlantLoop:EIR:Cooling');

  // HeatPump:PlantLoop:EIR:* - Insert blank condenser type field at position 4
  objectRules.set('HEATPUMP:WATERTOWATER:EIR:HEATING', {
    sourceClass: 'HeatPump:WaterToWater:EIR:Heating',
    targetClass: 'HeatPump:PlantLoop:EIR:Heating',
    fieldMappings: [
      ...createCopyMappings(0, 3), // Fields 0-2
      { sourceIndex: -1, targetIndex: 3, type: 'add_default', defaultValue: '' },
      ...createShiftedCopyMappings(3, 4, 50),
    ],
  });

  objectRules.set('HEATPUMP:WATERTOWATER:EIR:COOLING', {
    sourceClass: 'HeatPump:WaterToWater:EIR:Cooling',
    targetClass: 'HeatPump:PlantLoop:EIR:Cooling',
    fieldMappings: [
      ...createCopyMappings(0, 3),
      { sourceIndex: -1, targetIndex: 3, type: 'add_default', defaultValue: '' },
      ...createShiftedCopyMappings(3, 4, 50),
    ],
  });

  // GlobalGeometryRules - Expand abbreviations
  objectRules.set('GLOBALGEOMETRYRULES', {
    sourceClass: 'GlobalGeometryRules',
    targetClass: 'GlobalGeometryRules',
    fieldMappings: [
      { sourceIndex: 0, targetIndex: 0, type: 'transform', transform: expandGeometryAbbreviation },
      { sourceIndex: 1, targetIndex: 1, type: 'transform', transform: expandGeometryAbbreviation },
      { sourceIndex: 2, targetIndex: 2, type: 'transform', transform: expandGeometryAbbreviation },
      { sourceIndex: 3, targetIndex: 3, type: 'transform', transform: expandGeometryAbbreviation },
      { sourceIndex: 4, targetIndex: 4, type: 'transform', transform: expandGeometryAbbreviation },
      ...createShiftedCopyMappings(5, 5, 5),
    ],
  });

  // Fuel type standardization for various objects
  const fuelTypeObjects: Array<{ name: string; indices: number[] }> = [
    { name: 'BOILER:HOTWATER', indices: [1] },
    { name: 'BOILER:STEAM', indices: [1] },
    { name: 'CHILLER:ENGINEDRIVEN', indices: [35] },
    { name: 'CHILLER:COMBUSTIONTURBINE', indices: [54] },
    { name: 'CHILLERHEATER:ABSORPTION:DIRECTFIRED', indices: [32] },
    { name: 'COIL:COOLING:DX:MULTISPEED', indices: [16] },
    { name: 'COIL:HEATING:DX:MULTISPEED', indices: [15] },
    { name: 'COIL:HEATING:FUEL', indices: [2] },
    { name: 'ENERGYMANAGEMENTSYSTEM:METEREDOUTPUTVARIABLE', indices: [4] },
    { name: 'EXTERIOR:FUELEQUIPMENT', indices: [1] },
    { name: 'FUELFACTORS', indices: [0] },
    { name: 'GENERATOR:COMBUSTIONTURBINE', indices: [21] },
    { name: 'GENERATOR:INTERNALCOMBUSTIONENGINE', indices: [19] },
    { name: 'GENERATOR:MICROTURBINE', indices: [11] },
    { name: 'HVACTEMPLATE:SYSTEM:VRF', indices: [36] },
    { name: 'HVACTEMPLATE:PLANT:BOILER', indices: [4] },
    { name: 'LIFECYCLECOST:USEPRICEESCALATION', indices: [1] },
    { name: 'LIFECYCLECOST:USEADJUSTMENT', indices: [1] },
    { name: 'OTHEREQUIPMENT', indices: [1] },
    { name: 'WATERHEATER:MIXED', indices: [10, 14, 17] },
    { name: 'WATERHEATER:STRATIFIED', indices: [16, 19, 23] },
    { name: 'ZONEHVAC:HYBRIDUNITARYHVAC', indices: [17, 18, 19] },
    { name: 'AIRCONDITIONER:VARIABLEREFRIGERANTFLOW', indices: [66] },
  ];

  for (const obj of fuelTypeObjects) {
    const maxIndex = Math.max(...obj.indices) + 10;
    const mappings = [];
    for (let i = 0; i <= maxIndex; i++) {
      if (obj.indices.includes(i)) {
        mappings.push({
          sourceIndex: i,
          targetIndex: i,
          type: 'transform' as const,
          transform: standardizeFuelType,
        });
      } else {
        mappings.push({ sourceIndex: i, targetIndex: i, type: 'copy' as const });
      }
    }
    objectRules.set(obj.name, {
      sourceClass: obj.name,
      targetClass: obj.name,
      fieldMappings: mappings,
    });
  }

  return {
    fromVersion: { major: 9, minor: 2 },
    toVersion: { major: 9, minor: 3 },
    objectRules,
    variableMappings,
    deletedObjects,
    renamedObjects,
  };
}
